<script setup lang="ts">
import ButtonTag from '~/components/shared/ButtonTag.vue'
import { useMoney } from '~/composables/money'
import { FEATURE_FLAGS } from '~/constants'
import SimulatorService from '~/services/simulator/Simulator'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import { calculateFirstDueDate } from '~/utils/simulation/calculations'
import { DUE_DAY_OPTIONS } from '~/utils/simulation/dueDay'

interface SelectOptions { name: string, value: number }

const props = defineProps<{
  selectedGracePeriod: SelectOptions
  selectedCommission: SelectOptions
  disableFields: boolean
  loadingDetails: boolean
  maxGracePeriod: number
  projectId: string
  downPayment?: number
  financingValue?: number
  selectGracePeriod: (...args: any) => void
  selectCommission: (...args: any) => void
  selectedDueDay?: number | null
  selectDueDay?: (day: number) => void
  defaultFirstDueDate?: string | null
}>()
const simulatorService = new SimulatorService()
const financingValue = ref(props.financingValue)
const downPayment = ref(props.downPayment)
const { getSelectedInstallment } = useResumeStore()
const commissionOptions: any = ref({})
const { formatMoney } = useMoney()
// Feature flags
const { data: shouldShowCommissionWithMoneyData } = useFeatureFlag<boolean>(FEATURE_FLAGS.SHOW_COMMISSION_MONEY, false)
const shouldShowCommissionWithMoneyValue = computed(() => shouldShowCommissionWithMoneyData.value || false)
const { data: enableDueDateChangeData } = useFeatureFlag<boolean>(FEATURE_FLAGS.ENABLE_DUE_DATE_CHANGE, false, true)
const enableDueDateChangeValue = computed(() => enableDueDateChangeData.value || false)

onMounted(async () => {
  commissionOptions.value = await mountCommissionList(shouldShowCommissionWithMoneyValue.value || false)
})

function mountGracePeriodList() {
  const grace_period: number[] = []
  let counter = 1

  const grace_in_months = props.maxGracePeriod / 30
  while (counter <= grace_in_months) {
    grace_period.push(counter)
    counter++
  }

  return grace_period.map((grace_period) => {
    return {
      name: `${grace_period} Meses`,
      value: grace_period,
    }
  })
}

async function mountCommissionList(shouldShowCommissionWithMoneyData: boolean) {
  if (shouldShowCommissionWithMoneyData) {
    const commissionValues = await simulatorService.get_commissions(props.projectId)
    if (commissionValues.data) {
      return commissionValues.data.map((el) => {
        return {
          name: `${el.commission} % = ${formatMoney(el.commission_raw)} `,
          value: el.commission,
        }
      })
    }
  }

  const defaultCommissions = [0, 1, 2, 3, 4, 5]

  return defaultCommissions.map((el) => {
    return {
      name: `${el} % `,
      value: el,
    }
  })
}

const gracePeriodOptions = mountGracePeriodList()

const dueDayOptions = DUE_DAY_OPTIONS

const { t } = useI18n()
const route = useRoute()
const isEditPage = 'edit' in route.query

const firstDueDate = computed(() => calculateFirstDueDate(
  props.defaultFirstDueDate,
  !props.disableFields,
  props.selectedGracePeriod,
  props.selectedDueDay,
))

const shouldShowDueDaySection = computed(() => {
  return enableDueDateChangeValue.value
})
</script>

<template>
  <div class="col-span-5">
    <h2 class="card-subtitle">
      {{ t('simulation.financing_options') }}
    </h2>
    <div
      :class="`${!disableFields ? 'disabled' : ''} grid grid-cols-2 gap-4 mt-2xs`"
    >
      <SolSelect
        v-if="gracePeriodOptions?.length"
        id="select-grace-period"
        :selected="selectedGracePeriod"
        label="Carência*"
        class="w-full"
        name="grace-period"
        size="medium"
        :disabled="!disableFields || loadingDetails"
        placeholder="Busque aqui"
        :options="gracePeriodOptions"
        @update:selected="selectGracePeriod"
      />
      <SolSelect
        v-if="commissionOptions?.length"
        id="select-commission"
        label="Sua Comissão*"
        :selected="getSelectedInstallment()?.is_from_suggested ? { name: `${getSelectedInstallment()?.commission || 0} %`, value: getSelectedInstallment()?.commission || 0 } : selectedCommission"
        class="w-full"
        size="medium" name="commission"
        placeholder="Busque aqui"
        :options="commissionOptions"
        :disabled="!disableFields || loadingDetails || getSelectedInstallment()?.is_from_suggested"
        @update:selected="selectCommission"
      />
    </div>

    <div v-if="shouldShowDueDaySection" class="flex flex-col gap-2 mt-4">
      <label class="text-sm font-medium text-neutral-low-dark">
        {{ t('project_data.form.due_day') }}*
      </label>
      <div class="flex items-center gap-8">
        <div class="flex items-center gap-1">
          <ButtonTag
            v-for="day in dueDayOptions"
            :key="day"
            :label="day.toString()"
            :active="selectedDueDay === day"
            variant="primary"
            :disabled="!disableFields || loadingDetails"
            @click="selectDueDay?.(day)"
          />
        </div>

        <div v-if="firstDueDate" class="flex justify-center items-center gap-2">
          <div class="flex flex-col items-start gap-1">
            <span class="fonts-body-small-bold text-neutral-low-medium">
              {{ t('project_data.form.first_due_date') }}:
            </span>
            <span class="fonts-body-small-regular text-neutral-low-medium">
              {{ firstDueDate }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isEditPage"
      class="edit-fields grid grid-cols-2 gap-4 mt-2xs"
    >
      <InputMoneyInput
        id="edit-financing-value"
        v-model="financingValue"
        size="medium"
        required
        name="financingValue"
        label="Valor do projeto"
        placeholder="Digite o valor do projeto*"
        :disabled="true"
      />
      <InputMoneyInput
        id="edit-down-payment"
        v-model="downPayment"
        size="medium"
        name="downPayment"
        label="Entrada"
        placeholder="Digite o valor da entrada*"
        :disabled="true"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.btn-selectable-left{
  @apply border border-brand-primary-light rounded-l-md;
  @apply p-nano cursor-pointer font-bold;
}

.btn-selectable-right{
  @apply border border-brand-primary-light rounded-r-md;
  @apply p-nano cursor-pointer font-bold;
}
.btn-selected{
  @apply bg-brand-secondary-pure border-brand-secondary-pure;

  .title {
    @apply text-neutral-high-pure;
  }

}
.buttons {
  @apply flex;
}

.disabled {
  @apply opacity-75;
}

:deep(.edit-fields) {
  label {
    color: #757575 !important;
    opacity: 75%;
  }
}
</style>
